import { Link, Typography } from '@mui/material';

import { FieldRow, FieldValue } from '~/components/common/detailFields';
import { HeaderField } from '~/components/common/HeaderField';
import type { PropertyDetails } from '~/components/portfolio/types';

/** The loader fills every one of these with `'-'` when the API has nothing. */
const present = (value: string | null | undefined) => (value && value !== '-' ? value : undefined);

const isEmailish = (value: string | undefined) => Boolean(value?.includes('@'));

/**
 * Who owns and looks after the property — portfolio, client, consultant, and the person
 * to call. In the header rather than a tab: none of it changes, and it is the context
 * every tab is read against. These are the legacy header's right-hand column, as fields;
 * the address it also carried is the line under the property's name a few rows up.
 */
export function PropertyIdentityFields({ property }: { property: PropertyDetails | null }) {
  const loaded = property !== null;

  const contact = property?.contact;
  // The property's own contact wins; the property manager's owner is the
  // fallback the legacy header used, and both can be blank.
  const contactName = present(contact?.name) ?? present(property?.owner.name);
  const contactEmail = present(contact?.email) ?? present(property?.owner.email);
  const contactPhone = present(contact?.phone) ?? present(property?.owner.phone);

  return (
    <FieldRow>
      <HeaderField label="Portfolio">
        <FieldValue loaded={loaded} value={present(property?.portfolio)} />
      </HeaderField>

      <HeaderField label="Client">
        <FieldValue loaded={loaded} value={present(property?.owner.client)} />
      </HeaderField>

      <HeaderField label="Assigned consultant">
        <FieldValue loaded={loaded} value={present(property?.consultant)} />
      </HeaderField>

      {/* Only a valid-looking address becomes a `mailto:` link, since one on a placeholder
          opens an empty draft. */}
      <HeaderField
        label="Contact"
        caption={
          isEmailish(contactEmail) ? (
            <Link color="inherit" href={`mailto:${contactEmail}`}>
              {contactEmail}
            </Link>
          ) : (
            contactEmail
          )
        }
      >
        <FieldValue loaded={loaded} value={contactName} />
      </HeaderField>

      <HeaderField label="Phone">
        {loaded && contactPhone ? (
          <Typography variant="body2">
            <Link color="inherit" href={`tel:${contactPhone}`}>
              {contactPhone}
            </Link>
          </Typography>
        ) : (
          <FieldValue loaded={loaded} value={contactPhone} />
        )}
      </HeaderField>
    </FieldRow>
  );
}
